/* eslint-disable */
import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";

import * as url from "helper/UrlHelper";
import { getData, postData, putData } from "utils/Gateway";
import { getCustomStyles } from "Config/Config";

const SavePageOffcanvas = ({ selectedPage = null, onSaved = () => {}, onClose = () => {} }) => {
  const token = localStorage.getItem("token");
  const { t } = useTranslation();

  const isEditMode = !!(selectedPage && selectedPage._id);

  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [baseurl, setBaseurl] = useState("");
  const [apibaseurl, setApibaseurl] = useState("");
  const [companycif, setCompanycif] = useState("");
  const [companyValue, setCompanyValue] = useState(null);
  const [companyList, setCompanyList] = useState([]);
  const [companyInput, setCompanyInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [nameError, setNameError] = useState("");
  const [slugError, setSlugError] = useState("");

  const generateSlug = (value) =>
    value
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");

  const handleNameChange = (e) => {
    const val = e.target.value;
    setName(val);
    if (!isEditMode) setSlug(generateSlug(val));
    if (nameError) setNameError("");
  };

  const handleSlugChange = (e) => {
    setSlug(generateSlug(e.target.value));
    if (slugError) setSlugError("");
  };

  const handleCompanyChange = (val) => {
    setCompanyValue(val);
    if (val?.cif) setCompanycif(val.cif);
    else if (!val) setCompanycif("");
  };

  const validate = () => {
    let valid = true;
    if (!name.trim()) { setNameError(t("Page name is required")); valid = false; }
    if (!slug.trim()) { setSlugError(t("Slug is required")); valid = false; }
    return valid;
  };

  const getAllCompanies = async (filtername = "") => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_COMPANY_LIST +
        `?token=${token}&filtername=${filtername}&modulefrom=backend`;
      const response = await getData(requestUrl);
      if (response.status) setCompanyList(response.data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSave = async () => {
    if (!validate()) return;
    try {
      setIsSaving(true);

      const payload = {
        name: name.trim(),
        slug,
        company: companyValue?.value || null,
        companycif: companycif.trim(),
        baseurl: baseurl.trim(),
        apibaseurl: apibaseurl.trim(),
      };

      let response;
      if (isEditMode) {
        const requestUrl =
          url.API_BASE_URL +
          url.API_UPDATE_WEBSITE_PAGE +
          `/${selectedPage._id}?token=${token}`;
        response = await putData(requestUrl, payload);
      } else {
        const requestUrl =
          url.API_BASE_URL + url.API_CREATE_WEBSITE_PAGE + `?token=${token}`;
        response = await postData(requestUrl, payload);
      }

      if (response.status) {
        onSaved(response.data);
        closeOffcanvas();
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSaving(false);
    }
  };

  const closeOffcanvas = () => {
    const el = document.getElementById("savePageOffcanvas");
    const bsOffcanvas =
      window.bootstrap && window.bootstrap.Offcanvas.getInstance(el);
    if (bsOffcanvas) bsOffcanvas.hide();
    onClose();
  };

  // Pre-fill form when editing
  useEffect(() => {
    if (selectedPage && selectedPage._id) {
      setName(selectedPage.name || "");
      setSlug(selectedPage.slug || "");
      setBaseurl(selectedPage.baseurl || "");
      setApibaseurl(selectedPage.apibaseurl || "");
      setCompanycif(selectedPage.companycif || "");

      // Restore company select value from populated company field
      if (selectedPage.company) {
        const matched = companyList.find(
          (c) => c.value === (selectedPage.company._id || selectedPage.company)
        );
        if (matched) {
          setCompanyValue(matched);
        } else {
          // company data came from populate: { _id, name }
          setCompanyValue({
            label: selectedPage.company.name || "",
            value: selectedPage.company._id || selectedPage.company,
          });
        }
      } else {
        setCompanyValue(null);
      }
    } else {
      resetFields();
    }
    setNameError("");
    setSlugError("");
  }, [selectedPage]);

  const resetFields = () => {
    setName("");
    setSlug("");
    setBaseurl("");
    setApibaseurl("");
    setCompanycif("");
    setCompanyValue(null);
    setNameError("");
    setSlugError("");
  };

  useEffect(() => {
    getAllCompanies();
  }, []);

  useEffect(() => {
    if (companyInput.length > 1) {
      getAllCompanies(companyInput);
    }
  }, [companyInput]);

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="savePageOffcanvas"
      aria-labelledby="savePageOffcanvasLabel"
    >
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <h5 className="offcanvas-title fw-semibold text-white" id="savePageOffcanvasLabel">
          <span className="material-symbols-outlined me-2 align-middle">
            {isEditMode ? "edit" : "add_circle"}
          </span>
          {isEditMode ? t("Update Page") : t("New Page")}
        </h5>
        <button
          type="button"
          className="btn-close btn-close-white"
          aria-label="Close"
          onClick={closeOffcanvas}
        />
      </div>

      <div className="offcanvas-body px-4 py-3 d-flex flex-column gap-3">

        {/* Page Name */}
        <div>
          <label className="form-label fw-semibold small">
            {t("Page Name")} <span className="text-danger">*</span>
          </label>
          <input
            type="text"
            className={`form-control rounded-3 ${nameError ? "is-invalid" : ""}`}
            placeholder={t("e.g. Home, Services, About")}
            value={name}
            onChange={handleNameChange}
          />
          {nameError && <div className="invalid-feedback">{nameError}</div>}
        </div>

        {/* Slug */}
        <div>
          <label className="form-label fw-semibold small">
            {t("Slug")} <span className="text-danger">*</span>
          </label>
          <div className="input-group">
            <span className="input-group-text text-muted small">/</span>
            <input
              type="text"
              className={`form-control rounded-end-3 ${slugError ? "is-invalid" : ""}`}
              placeholder="services"
              value={slug}
              onChange={handleSlugChange}
            />
            {slugError && <div className="invalid-feedback">{slugError}</div>}
          </div>
          <p className="text-muted small mt-1">
            {t("URL-friendly identifier — used to serve this page on your domain")}
          </p>
        </div>

        {/* Base URL */}
        <div>
          <label className="form-label fw-semibold small">{t("Base URL")}</label>
          <input
            type="text"
            className="form-control rounded-3"
            placeholder={t("e.g. https://mysite.com")}
            value={baseurl}
            onChange={(e) => setBaseurl(e.target.value)}
          />
          <p className="text-muted small mt-1">
            {t("Domain or subdomain where this page will be served")}
          </p>
        </div>

        {/* API Base URL */}
        <div>
          <label className="form-label fw-semibold small">{t("API Base URL")}</label>
          <input
            type="text"
            className="form-control rounded-3"
            placeholder={t("e.g. https://api.myserver.com/api")}
            value={apibaseurl}
            onChange={(e) => setApibaseurl(e.target.value)}
          />
          <p className="text-muted small mt-1">
            {t("URL of the API server — used in the downloaded page shell to fetch content")}
          </p>
        </div>

        {/* Company */}
        <div>
          <label className="form-label fw-semibold small">{t("Company")}</label>
          <Select
            styles={getCustomStyles()}
            options={companyList}
            value={companyValue}
            onChange={handleCompanyChange}
            onInputChange={setCompanyInput}
            isClearable
            placeholder={t("Select or type company name")}
          />
          <p className="text-muted small mt-1">
            {t("Associates this page with a company and auto-fills the CIF")}
          </p>
        </div>

        {/* Company CIF */}
        <div>
          <label className="form-label fw-semibold small">{t("Company CIF")}</label>
          <input
            type="text"
            className="form-control rounded-3"
            placeholder={t("e.g. B12345678")}
            value={companycif}
            onChange={(e) => setCompanycif(e.target.value)}
          />
          <p className="text-muted small mt-1">
            {t("Used by the public API to identify which pages belong to this company")}
          </p>
        </div>

      </div>

      <div className="offcanvas-footer border-top px-4 py-3 d-flex gap-2 justify-content-end">
        <button
          type="button"
          className="btn btn-light border rounded-3 px-4"
          onClick={closeOffcanvas}
        >
          {t("Cancel")}
        </button>
        <button
          type="button"
          className="btn btn-primary rounded-3 px-4"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              {isEditMode ? t("Saving…") : t("Creating…")}
            </>
          ) : (
            isEditMode ? t("Save Changes") : t("Create Page")
          )}
        </button>
      </div>
    </div>
  );
};

export default SavePageOffcanvas;
